import { useSmartMutation, useSmartQuery } from "@shared/api/queries/hooks";
import { queryClient } from "@shared/api/queries/queryClient";
import { QueryKeyFactory } from "@shared/api/queries/queryKeyFactory";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "@shared/ui/use-toast";

import { coreApi } from "./api";
import { Utility } from "./types";
import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";
import { getNscResponseTimeConfiguration } from "@features/cx/settings/api";

const UTILITY_STORAGE_KEY = "utility";

// export const useUtility = (tenantId: string) => {
//   const navigate = useNavigate();

//   const [selectedUtility, setSelectedUtility] = useState<string | null>(null);
//   const [selectedUtilityObject, setSelectedUtilityObject] =
//     useState<Utility | null>(null);
//   const [error, setError] = useState<string | null>(null);

//   const { data: utilities = [], isLoading } = useQuery<Utility[], Error>({
//     queryKey: ["utilities", tenantId],
//     queryFn: () => coreApi.getUtility(tenantId),
//     enabled: !!tenantId, // Only fetch when tenantId exists
//   });
//   console.log("Fetched utilities:", utilities);

//   // Initialize utility selection when utilities are loaded
//   useEffect(() => {
//     if (utilities.length > 0) {
//       initializeUtilitySelection();
//     }
//   }, [utilities]);

//   // Show error toast when error occurs
//   useEffect(() => {
//     if (error) {
//       toast({
//         title: "Error",
//         description: error || "Something went wrong",
//         variant: "destructive",
//       });
//     }
//   }, [error]);

//   const getStoredUtility = (): Utility | null => {
//     try {
//       const storedUtility = localStorage.getItem(UTILITY_STORAGE_KEY);
//       return storedUtility ? JSON.parse(storedUtility) : null;
//     } catch (error) {
//       console.error("Error parsing stored utility:", error);
//       setError("Failed to load stored utility");
//       return null;
//     }
//   };

//   const initializeUtilitySelection = () => {
//     // Check if utility is already stored in localStorage
//     const storedUtility = getStoredUtility();

//     if (storedUtility) {
//       // Verify that the stored utility still exists in the utilities list
//       const utilityExists = utilities.find(
//         (utility) => utility.id === storedUtility.id
//       );

//       if (utilityExists) {
//         // Use stored utility
//         setSelectedUtility(storedUtility.name);
//         setSelectedUtilityObject(storedUtility);
//         return;
//       } else {
//         // Remove invalid stored utility
//         localStorage.removeItem(UTILITY_STORAGE_KEY);
//       }
//     }

//     // Set default utility (2nd item like in Angular code, or 1st if only one exists)
//     const defaultUtility = utilities.length > 1 ? utilities[1] : utilities[0];
//     console.log("Default utility selected:", defaultUtility);
//     if (defaultUtility) {
//       setUtilityValue(defaultUtility);
//     }
//   };

//   const setUtilityValue = (utility: Utility) => {
//     try {
//       // Store in localStorage
//       localStorage.setItem(UTILITY_STORAGE_KEY, JSON.stringify(utility));

//       // Update state
//       setSelectedUtility(utility.name);
//       setSelectedUtilityObject(utility);

//       setError(null); // Clear any previous errors
//     } catch (error) {
//       console.error("Error storing utility:", error);
//       setError("Failed to save utility selection");
//     }
//   };

//   const handleUtilityChange = (utilityName: string) => {
//     const selectedUtilityObj = utilities.find(
//       (utility) => utility.name === utilityName
//     );

//     if (selectedUtilityObj) {
//       setUtilityValue(selectedUtilityObj);
//       navigate(0); // Refresh current route without changing URL
//     } else {
//       setError("Selected utility not found in utilities list");
//       console.error("Selected utility not found in utilities list");
//     }
//   };

//   const clearUtility = () => {
//     try {
//       localStorage.removeItem(UTILITY_STORAGE_KEY);
//       setSelectedUtility(null);
//       setSelectedUtilityObject(null);
//       setError(null);
//     } catch (error) {
//       console.error("Error clearing utility:", error);
//       setError("Failed to clear utility data");
//     }
//   };

//   return {
//     utilities,
//     selectedUtility,
//     selectedUtilityObject, // New: provides the full utility object
//     setSelectedUtility: handleUtilityChange,
//     setUtilityValue, // New: directly set utility object
//     clearUtility, // New: clear utility data (useful for logout)
//     loading: isLoading,
//     error,
//   };
// };



export const useUtility = (tenantId: string) => {
  const navigate = useNavigate();

  const [selectedUtility, setSelectedUtility] = useState<string | null>(null);
  const [selectedUtilityObject, setSelectedUtilityObject] = useState<Utility | null>(null);
  const [error, setError] = useState<string | null>(null);

  const { data: utilities = [], isLoading } = useQuery<Utility[], Error>({
    queryKey: ["utilities", tenantId],
    queryFn: () => coreApi.getUtility(tenantId),
    enabled: !!tenantId,
  });

  // Initialize utility when data loads
  useEffect(() => {
    if (utilities.length > 0) {
      initializeUtilitySelection();
    }
  }, [utilities]);

  // Show error toast
  useEffect(() => {
    if (error) {
      toast({
        title: "Error",
        description: error || "Something went wrong",
        variant: "destructive",
      });
    }
  }, [error]);

  const getStoredUtility = (): Utility | null => {
    try {
      const storedUtility = localStorage.getItem(UTILITY_STORAGE_KEY);
      return storedUtility ? JSON.parse(storedUtility) : null;
    } catch (error) {
      console.error("Error parsing stored utility:", error);
      setError("Failed to load stored utility");
      return null;
    }
  };

  const initializeUtilitySelection = () => {
    const storedUtility = getStoredUtility();

    if (storedUtility) {
      const utilityExists = utilities.find((u) => u.id === storedUtility.id);
      if (utilityExists) {
        setSelectedUtility(storedUtility.name);
        setSelectedUtilityObject(storedUtility);

        // ✅ Fetch NSC config if not stored
        fetchNscConfigIfNeeded(storedUtility.id);
        return;
      } else {
        localStorage.removeItem(UTILITY_STORAGE_KEY);
      }
    }

    // Default utility (2nd or 1st)
    const defaultUtility = utilities.length > 1 ? utilities[1] : utilities[0];
    if (defaultUtility) {
      setUtilityValue(defaultUtility);
    }
  };

  const setUtilityValue = (utility: Utility) => {
    try {
      localStorage.setItem(UTILITY_STORAGE_KEY, JSON.stringify(utility));
      setSelectedUtility(utility.name);
      setSelectedUtilityObject(utility);
      setError(null);

      // ✅ Fetch NSC config if not stored
      fetchNscConfigIfNeeded(utility.id);
    } catch (error) {
      console.error("Error storing utility:", error);
      setError("Failed to save utility selection");
    }
  };

  const fetchNscConfigIfNeeded = async (utilityId: string | number) => {
    const storedConfig = localStorage.getItem("NSC-Configuration");
    if (storedConfig) return; // ✅ Already saved, skip API call

    try {
      const response = await getNscResponseTimeConfiguration(String(utilityId));

      localStorage.setItem("NSC-Configuration", JSON.stringify(response));
      console.log("NSC-Configuration", JSON.stringify(response));

      console.log("✅ NSC configuration saved to localStorage");
    } catch (err) {
      console.error("❌ Failed to fetch NSC configuration:", err);
    }
  };

  const handleUtilityChange = (utilityName: string) => {
    const selectedUtilityObj = utilities.find((u) => u.name === utilityName);

    if (selectedUtilityObj) {
      setUtilityValue(selectedUtilityObj);
      navigate(0); // Refresh
    } else {
      setError("Selected utility not found in utilities list");
    }
  };

  const clearUtility = () => {
    try {
      localStorage.removeItem(UTILITY_STORAGE_KEY);
      localStorage.removeItem("NSC-Configuration"); // optional: clear config too
      setSelectedUtility(null);
      setSelectedUtilityObject(null);
      setError(null);
    } catch (error) {
      console.error("Error clearing utility:", error);
      setError("Failed to clear utility data");
    }
  };

  return {
    utilities,
    selectedUtility,
    selectedUtilityObject,
    setSelectedUtility: handleUtilityChange,
    setUtilityValue,
    clearUtility,
    loading: isLoading,
    error,
  };
};



export const useSessions = () => {
  return useSmartQuery(QueryKeyFactory.module.core.sessions(), () =>
    coreApi.getSessions()
  );
};

export const useDeleteSessions = () => {
  return useSmartMutation(
    async ({ id }: { id: string | number }) => {
      return coreApi.deleteSessions(id);
    },
    {
      onSuccess: () => {
        console.log("Session deleted successfully"),
          queryClient.refetchQueries({
            queryKey: QueryKeyFactory.module.core.sessions().slice(0, 1),
            exact: false,
          });
      },
    }
  );
};

export const useDeleteAllSessions = () => {
  return useSmartMutation(
    async () => {
      return coreApi.deleteSessions();
    },
    {
      onSuccess: () => {
        console.log("Session deleted successfully"),
          queryClient.refetchQueries({
            queryKey: QueryKeyFactory.module.core.sessions().slice(0, 1),
            exact: false,
          });
      },
    }
  );
};

// export const useRolesPermission = (remoteUtilityId:string) => {
//   // const remoteUtilityId = useRemoteUtilityId();

//   const key = QueryKeyFactory.module.onboarding.userRolePermission();

//   return useSmartQuery(
//     key,
//     () =>  coreApi.getUserRolePermission(remoteUtilityId),
//     {
//       enabled: !!remoteUtilityId,
//     }
//   );
// };

export const useRolesPermissionAccess = (remoteUtilityId: string) => { 
  const key = QueryKeyFactory.module.onboarding.userRolePermissionAccess(); 
  
  console.log('useRolesPermission called with:', {
    remoteUtilityId,
    type: typeof remoteUtilityId,
    enabled: !!remoteUtilityId && remoteUtilityId !== 'undefined'
  });
 
  return useSmartQuery( 
    key, 
    () => {
      console.log('Query function executing with utilityId:', remoteUtilityId);
      return coreApi.getUserRolePermissionAccess(remoteUtilityId);
    }, 
    { 
      enabled: !!remoteUtilityId && remoteUtilityId !== 'undefined',
      retry: false,
    } 
  ); 
};